import React from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  IAcitvityEmailConversationLogsProps,
} from "./Activity.types";
import { LoadState } from "../../constants/enums";
import { useCustomerFullInfo, useDispatchWrapper } from "../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import { fetchEmailConversationListAsync } from "../../redux/activity/activityActions";
import { EmailsHistoryLogRightPanel } from "../../views/emailsHistoryLog/EmailsHistoryLogRightPanel";
import { IEmailHistoryLog } from "../../redux/emails/Emails.types";
import { useTableV2State } from "../TableV2/hooks/useTableV2State";
import { TABLE_CACHING_KEYS } from "../../constants/constants";
import { DataTableV2RowRenderType, IDataTableV2Props } from "../TableV2/interfaces/IDataTableV2Props";
import { StandardTableActions } from "../StandardTableActions/StandardTableActions";
import { clearEmailsState } from "../../redux/emails/EmailsAction";
import { PageContainer } from "../container/PageContainer";
import { PageTitleBar } from "../PageTitleBar/PageTitleBar";
import { DataTableV2 } from "../TableV2/DataTableV2";
import { Send, SendOutlined } from "@mui/icons-material";
import { EmailComposeDialog } from "../MessagesDialogs/EmailCompose/EmailComposeDialog";
import { SendEmailDialog } from "../../views/email/Components/SendEmailDialog/SendEmailDialog";
import { outlookInitiateAuth } from "../../redux/outlook/outlookThunk";
import { triggerErrorDialogMessages } from "../../helpers";
import { APIError } from "../../constants/types";

export const ActivityEmailConversationLogs: React.FC<
  IAcitvityEmailConversationLogsProps
> = (props) => {
  const { list, loading } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.emailConversation
  );
  const {
    email
  } = useCustomerFullInfo();

  const { moduleInformation, isEmailButton } = props;

  const dispatch = useDispatchWrapper();

  const [openEmail, setOpenEmail] = React.useState<IEmailHistoryLog | null>(null);
  const [openSendEmail, setOpenSendEmail] = React.useState(false);
  const [openOutlookEmail, setOpenOutlookEmail] = React.useState(false);
  const [showOutlookPrompt, setShowOutlookPrompt] = React.useState(false);

  const isOutlookConnected = useSelector(
    (storeState: IStoreState) => storeState.outlook.connection.connected
  );

  const {
    state: {
      dateState,
      searchState,
      tabs,
      columnsConfig,
      pagination,
      masterColumnResize,
      dense
    },
    setDateState,
    setSelectedTab,
    setTableTabs,
    setSearchState,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    onDenseToggle,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last28Days",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      extraFetchFactor: 1,
      apiFetchRowCount: 26,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.EMAILS_HISTORY_LOG,
    },
  });

  const fetchList = async () => {
    dispatch(fetchEmailConversationListAsync(moduleInformation.moduleId));
  };

  const handleComplete = () => {
    setOpenSendEmail(false);
    fetchList();
  }

  const handleOutlookEmailClick = () => {
    if (!isOutlookConnected) {
      setShowOutlookPrompt(true);
      return;
    }
    setOpenOutlookEmail(true);
  };

  const handleInitiateOutlookAuth = async () => {
    try {
      const res = await dispatch(
        outlookInitiateAuth({ redirect_uri: window.location.origin + "/api/v1" })
      ).unwrap();
      window.location.href = res.url;
    } catch (err) {
      triggerErrorDialogMessages(err as APIError, dispatch);
    }
  };

  const handleOutlookEmailComplete = () => {
    setOpenOutlookEmail(false);
    fetchList();
  };

  const emailsHistoryLogTableProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading !== LoadState.Loaded,
    uniqueRowKeyName: "id",

    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        disableResize: true,
        isFirstColumnSticky: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        headingAlign: "center",
        width: "100px",
        onRowCellRender: (value, row: IEmailHistoryLog) => {
          return <StandardTableActions onViewClick={() => setOpenEmail(row)} />;
        },
      },

      {
        key: "email_history_code",
        headerName: "Code",
        fieldName: "email_history_code",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 15,
        enableSorting: true,
      },
      {
        key: "module_name",
        headerName: "Module Name",
        fieldName: "module_name",
        renderType: DataTableV2RowRenderType.CHIP_WARNING,
        // exportCellWidth: 15,
        enableSorting: true,
      },

      {
        key: "from_email",
        headerName: "From",
        fieldName: "from_email",
        width: "320px",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 15,
        enableSorting: true,
      },
      {
        key: "to_mail_ids",
        headerName: "To",
        fieldName: "to_mail_ids",
        width: "320px",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 15,
        enableSorting: true,
        onRowCellRender: (value, row: IEmailHistoryLog) => {
          return row.to_mail_ids.length === 0 ? (
            <Typography variant="body1">--</Typography>
          ) : (
            row.to_mail_ids.map((email, index) => (
              <Chip label={email} key={index} />
            ))
          );
        },
      },
      {
        key: "subject",
        headerName: "Subject",
        fieldName: "subject",
        width: "380px",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 15,
        enableSorting: true,
      },
      {
        key: "comment",
        headerName: "Comment",
        fieldName: "comment",
        width: "380px",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 15,
        enableSorting: true,
      },
      {
        key: "create_ts",
        headerName: "Date Modified",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 15,
        enableSorting: true,
      },
      {
        key: "insert_ts",
        headerName: "Date Created",
        fieldName: "insert_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 15,
        enableSorting: true,
      },
    ],
    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,

    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
    dense: dense,
    onDenseToggle: onDenseToggle,
  };


  React.useEffect(() => {
    fetchList();
  }, []);

  React.useEffect(() => {
    return () => {
      dispatch(clearEmailsState());
    };
  }, []);

  return (
    <PageContainer
      title="View Email Log"
      description="this is innerpage"
    >


      <PageTitleBar
         rightHeading={
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<Send/>}
            onClick={()=> setOpenSendEmail(true)}
            >Send Email</Button>
            <Button variant="contained" startIcon={<SendOutlined/>}
            onClick={handleOutlookEmailClick}
            >Send Email To Outlook</Button>
          </Stack>
         }
      />
      <DataTableV2 {...emailsHistoryLogTableProps} />

      {openEmail && (
        <EmailsHistoryLogRightPanel
          open={true}
          emailHistoryLog={openEmail}
          onClose={() => setOpenEmail(null)}
        />
      )

      }

      {openSendEmail &&
         <EmailComposeDialog
            open={openSendEmail}
            enableAttachments={true}
            onClose={()=> setOpenSendEmail(false)}
            toEmail={email}
            onComplete={handleComplete}
         />

      }

      {openOutlookEmail && (
        <SendEmailDialog
          open={openOutlookEmail}
          initialTo={email}
          onClose={() => setOpenOutlookEmail(false)}
          onComplete={handleOutlookEmailComplete}
        />
      )}

      <Dialog
        open={showOutlookPrompt}
        onClose={() => setShowOutlookPrompt(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Connect Outlook</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You need to connect your Microsoft Outlook account before sending emails.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOutlookPrompt(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInitiateOutlookAuth}>
            Connect Outlook
          </Button>
        </DialogActions>
      </Dialog>

    </PageContainer>
  );
};